import { httpInterceptor } from '../http-interceptor';

/**
 * Backend contract (to be implemented in API):
 * - GET  spvp-asp/mappings?customer=<id>                  -> { profiles: AspProfileDto[] }
 * - POST spvp-asp/mappings/upsert                         -> { profile: AspProfileDto }
 * - POST spvp-asp/mappings/delete                         -> { ok: true }
 * - GET  planner/loadboard-schema?customerCode=<id>       -> { schemaJson: string }
 * - GET  planner/aspforms_download_common_template      -> { fileName, fileBase64 } COMMON template from OneDrive
 * - POST planner/aspforms_generate_and_upload           -> upload filled ASP Excel (GenerateAspFormToOneDriveRequest)
 *
 * schemaJson is stringified JSON:
 *   { "columns": LoadBoardColumnDto[], "headerRows"?: any[], "shipmentForm"?: ShipmentFormLayoutDto }
 * shipmentForm (optional) drives the Load Board "Shipment Form" header + Excel block per customer.
 * It can be merged from dbo.Customer (e.g. AspLoadBoardConfigJson) when building the response.
 *
 * Notes:
 * - We keep the same profile shape that the UI already uses today (mirrors `spvp-asp-shared.js`).
 * - Backend should store per-customer (and optionally per-shipTo) mappings.
 */
export class SpvpAspService {
  static ENDPOINTS = {
    GET_ASP_MAPPING_STORE: 'planner/asp-mapping-store',
    SAVE_ASP_MAPPING_STORE: 'planner/asp-mapping-store/save',
    GET_LOADBOARD_SCHEMA: 'planner/loadboard-schema',
    SAVE_LOADBOARD_SCHEMA: 'planner/loadboard-schema/save',
    GET_CUSTOMER_ASP_LOADBOARD_CONFIG: 'planner/customer-asp-loadboard-config',
    GENERATE_AND_UPLOAD_ASP_FORM: 'planner/aspforms_generate_and_upload',
    DOWNLOAD_COMMON_ASP_TEMPLATE: 'planner/aspforms_download_common_template',
  };

  static async getAspMappingStore({ customerCode, shipToCode = null }) {
    return httpInterceptor.get(this.ENDPOINTS.GET_ASP_MAPPING_STORE, {
      customerCode,
      ...(shipToCode ? { shipToCode } : {}),
    });
  }

  static async saveAspMappingStore({ customerCode, shipToCode = null, storeVersion = 1, storeJson }) {
    return httpInterceptor.post(this.ENDPOINTS.SAVE_ASP_MAPPING_STORE, {
      customerCode,
      shipToCode,
      storeVersion,
      storeJson,
    });
  }

  static async getLoadBoardSchema({ customerCode, shipToCode = null }) {
    return httpInterceptor.get(this.ENDPOINTS.GET_LOADBOARD_SCHEMA, {
      customerCode,
      ...(shipToCode ? { shipToCode } : {}),
    });
  }

  /**
   * Per-customer shipment form + optional template metadata from dbo.Customer.
   * Route: GET api/planner/customer-asp-loadboard-config/{customerCode}
   */
  static async getCustomerAspLoadBoardConfig(customerCode) {
    const code = encodeURIComponent(String(customerCode ?? "").trim());
    return httpInterceptor.get(`${this.ENDPOINTS.GET_CUSTOMER_ASP_LOADBOARD_CONFIG}/${code}`, {});
  }

  static async saveLoadBoardSchema({ customerCode, shipToCode = null, schemaVersion = 1, schemaJson }) {
    return httpInterceptor.post(this.ENDPOINTS.SAVE_LOADBOARD_SCHEMA, {
      customerCode,
      shipToCode,
      schemaVersion,
      schemaJson,
    });
  }

  /**
   * Download COMMON_ASP_Form.xlsx from OneDrive (Viji) as base64.
   * Route: GET api/planner/aspforms_download_common_template
   * Response.response: { fileName, fileBase64 }
   */
  static async downloadCommonAspTemplate(options = {}) {
    return httpInterceptor.get(
      this.ENDPOINTS.DOWNLOAD_COMMON_ASP_TEMPLATE,
      {},
      {
        timeoutMs: typeof options.timeoutMs === 'number' ? options.timeoutMs : 120000,
      },
    );
  }

  /**
   * Upload a NEW filled ASP Excel into Loaded_ASP_Forms_Raintels only.
   * Must never overwrite ASP_Forms_Raintels/COMMON_ASP_Form.xlsx.
   * Route: POST api/planner/aspforms_generate_and_upload
   */
  static async generateAndUploadAspForm(payload, options = {}) {
    const fileName = String(payload?.fileName || '').trim();
    if (!fileName) {
      throw new Error('fileName is required for ASP form upload.');
    }
    if (/^common_asp_form\.xlsx$/i.test(fileName)) {
      throw new Error('Refusing to upload as COMMON_ASP_Form.xlsx — master template must not be overwritten.');
    }
    return httpInterceptor.post(
      this.ENDPOINTS.GENERATE_AND_UPLOAD_ASP_FORM,
      {
        ...payload,
        fileName,
        outputFolderName: payload?.outputFolderName || 'Loaded_ASP_Forms_Raintels',
        neverOverwriteCommonTemplate: true,
      },
      {
        timeoutMs: typeof options.timeoutMs === 'number' ? options.timeoutMs : 120000,
      },
    );
  }
}

